import { ChatMessageStatus, type PpvMessagePreviewResponseDto } from '@api'
import { type ColumnDef } from '@tanstack/react-table'
import { formatDistanceToNow } from 'date-fns'
import { fromZonedTime } from 'date-fns-tz'

import DoubleCheckIcon from '@/assets/icons/icon-double-check.svg'
import InfoIcon from '@/assets/icons/icon-info.svg'
import { Tooltip } from '@/components/ui/tooltip'
import { type Media } from '@/modules/chatting/types'
import { classNames } from '@/utils/helpers'

import { ChatMessageLink } from '../chat-message-link/ChatMessageLink'
import { OverviewMedia } from '../overview-media/OverviewMedia'

export const getPpvOverviewColumns = ({
  onClose,
}: {
  onClose: () => void
}): ColumnDef<PpvMessagePreviewResponseDto>[] => [
  {
    accessorKey: 'fanName',
    header: 'Fan',
    size: 160,
    enableSorting: false,
    cell: ({ row }) => (
      <div>{row.original.fanName || row.original.fanDisplayName}</div>
    ),
  },
  {
    accessorKey: 'sentAt',
    header: 'PPV Sent',
    size: 140,
    enableSorting: false,
    cell: ({ getValue }) => {
      const dateObj = fromZonedTime(new Date(getValue<string>()), 'UTC')

      return (
        <span className="whitespace-nowrap">
          {formatDistanceToNow(dateObj)} ago
        </span>
      )
    },
  },
  {
    accessorKey: 'media',
    header: 'Attachment',
    size: 190,
    enableSorting: false,
    cell: ({ getValue }) => <OverviewMedia media={getValue<Media[]>()} />,
  },
  {
    accessorKey: 'grossPrice',
    header: 'Price',
    size: 80,
    enableSorting: false,
    cell: ({ getValue }) => (getValue() ? `$${getValue()}` : ''),
  },
  {
    accessorKey: 'status',
    header: () => {
      const tooltip = (
        <div className="flex flex-col gap-2 p-1">
          {MESSAGE_STATUS_OPTIONS.map(({ text, color }) => (
            <div
              key={text}
              className="flex items-center gap-2"
            >
              <DoubleCheckIcon className={classNames(color, 'h-5 w-5')} />
              <span className="text-sm">{text}</span>
            </div>
          ))}
        </div>
      )

      return (
        <div className="flex items-center gap-1">
          Status
          <Tooltip
            content={tooltip}
            side="bottom"
          >
            <InfoIcon />
          </Tooltip>
        </div>
      )
    },
    size: 90,
    enableSorting: false,
    cell: ({ getValue }) => {
      const status = getValue<ChatMessageStatus>()
      const statusOption = MESSAGE_STATUS_OPTIONS.find(
        (option) => status === option.value,
      )

      return (
        <DoubleCheckIcon
          className={classNames(statusOption?.color, 'ml-4 h-5 w-5')}
        />
      )
    },
  },
  {
    accessorKey: 'chatId',
    header: 'Go to Chat',
    size: 112,
    enableSorting: false,
    cell: ({ row }) => {
      const { onlyfansFanId, onlyfansMessageId } = row.original

      return (
        <div onClick={onClose}>
          <ChatMessageLink
            onlyfansFanId={onlyfansFanId}
            onlyfansMessageId={onlyfansMessageId}
          />
        </div>
      )
    },
  },
]

export const MESSAGE_STATUS_OPTIONS = [
  {
    value: ChatMessageStatus.NOT_READ,
    text: 'Not seen',
    color: 'text-secondary-300',
  },
  {
    value: ChatMessageStatus.READ,
    text: 'Seen',
    color: 'text-primary-500',
  },
  {
    value: ChatMessageStatus.ANSWERED,
    text: 'Answered',
    color: 'text-green-500',
  },
  { value: ChatMessageStatus.BOUGHT, text: 'Bought', color: 'text-yellow-500' },
]
